import {Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse, HttpParams, HttpResponse} from "@angular/common/http";
import {catchError, Observable, of, retry, throwError} from "rxjs";
import {MessageService} from "primeng/api";
import {formatString} from "../../shared/utils/string.utils";
import {SessionRefreshError} from "./session-refresh-error";

/** No response (network failure, status 0) or a server-side failure (5xx) — a repeat of the request may succeed. */
export function mayPassOnRetry(error: unknown): boolean {
  return !(error instanceof HttpErrorResponse) || error.status === 0 || error.status >= 500;
}

/**
 * One repeat of a read. A `SessionRefreshError` is not repeated: Identity has already answered (or not) for the whole
 * tab, a second read would only start the same failed refresh again.
 */
export function retryReadOnce<T>() {
  return retry<T>({count: 1, delay: error => error instanceof SessionRefreshError ? throwError(() => error) : of(0)});
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  constructor(private http: HttpClient, private messageService: MessageService) {}

  // Reads (`get`, `downloadFile`) retry once on failure; mutating requests are sent exactly once —
  // a retried POST/PUT/DELETE could apply the change twice (ADR 2026-09-03).

  public get<T>(endpoint: string, data: any = {}, useDefaultHttpParams?: boolean): Observable<T> {
    return this.http
      .get<T>(endpoint, {params: useDefaultHttpParams ? data : this.toHttpParams(data)})
      .pipe(retryReadOnce(), catchError(err => this.handleError(err, this.messageService)));
  }

  public downloadFile(endpoint: string, data: any = {}, useDefaultHttpParams?: boolean): Observable<HttpResponse<Blob>> {
    return this.http
      .get(endpoint, { params: useDefaultHttpParams ? data : this.toHttpParams(data), observe: 'response', responseType: 'blob' })
      .pipe(retryReadOnce(), catchError(err => this.handleError(err, this.messageService)));
  }

  public post<T>(endpoint: string, data: any = {}): Observable<T> {
    return this.http.post<T>(endpoint, data)
      .pipe(catchError(err => this.handleError(err, this.messageService)));
  }

  // Only caller — the Identity token request (IdentityService: login and token refresh). It keeps the single
  // retry, the one exception to "retry reads only": the request changes nothing irreversibly, and a refresh
  // that fails without a retry would end in a failed session refresh. The retry covers only a failure that may pass
  // on its own — no response (status 0) or 5xx (PAR-12961): a 4xx is the server's answer (wrong password, revoked
  // refresh token), resending it changes nothing and sent a wrong password twice.
  // `passThrough` — errors the caller shows itself: no common toast, the original HttpErrorResponse goes on
  // (the login page shows a rejected password above the fields; the token refresh decides between leaving the
  // session and the one "couldn't refresh" message, PAR-13011). Other errors take the common path as before.
  public postWithHeaders<T>(endpoint: string, data: any = {}, options: any = {},
                            passThrough?: (error: HttpErrorResponse) => boolean): Observable<T> {
    return this.http.post<T>(endpoint, data, {headers: options, responseType: "json"})
      .pipe(
        retry({count: 1, delay: error => mayPassOnRetry(error) ? of(0) : throwError(() => error)}),
        catchError(err => err instanceof HttpErrorResponse && passThrough?.(err)
          ? throwError(() => err)
          : this.handleError(err, this.messageService)));
  }

  public clear<T>(endpoint: string, data: any): Observable<T> {
    return this.http.delete<T>(formatString(endpoint, <any>data))
      .pipe(catchError(err => this.handleError(err, this.messageService)));
  }

  public delete<T>(endpoint: string, data: T): Observable<any> {
    return this.http.delete<any>(formatString(endpoint, <any>data))
      .pipe(catchError(err => this.handleError(err, this.messageService)));
  }

  // PAR-12347 — no existing caller of `delete<T>` above sends a JSON body (it only fills a
  // `formatString` URL template); `DELETE positions` (OrderAssemblyController) needs one
  // (`{orderId, itemId}`), so this uses `HttpClient.request('delete', ...)` — Angular's documented way
  // to attach a body to a DELETE request (`HttpClient.delete` itself takes no data argument).
  public deleteWithBody<T>(endpoint: string, data: any): Observable<T> {
    return this.http.request<T>('delete', endpoint, {body: data})
      .pipe(catchError(err => this.handleError(err, this.messageService)));
  }

  public put<TResult>(endpoint: string, data: any): Observable<TResult> {
    return this.http.put<TResult>(formatString(endpoint, data), data)
      .pipe(catchError(err => this.handleError(err, this.messageService)));
  }

  /** Public since PAR-12794: `RoyaltyService.exportCsv` bypasses `get`/`downloadFile` (it needs the
   *  raw `HttpErrorResponse` with its Blob body) but must put the very same wire shape on the query
   *  string — arrays as `key[i]=value`, blanks dropped — so it reuses this builder instead of copying it. */
  public toHttpParams(object: any): HttpParams {
    let params = new HttpParams();
    const keys = Object.keys(object);

    keys.forEach((x: string) => {
      if (!object.hasOwnProperty(x)) {
        return;
      }
      if (object[x] !== null && object[x] !== undefined && object[x].toString() !== '') {
        if (object[x] instanceof Array) {
          object[x].forEach((item, index) => {
            params = params.append(`${x.toString()}[${index}]`, item);
          });
        } else if (object[x] instanceof Object) {
          const children = this.toHttpParams(object[x]);
          children.keys().forEach(key => {
            params = params.append(x + '.' + key, children.get(key) as string);
          });
        } else {
          params = params.append(x, object[x]);
        }
      }
    });
    return params;

  }

  // Error handling
  handleError(error: any, messageService: MessageService) {
    // PAR-13011: the failed token refresh has already been shown to the user once (IdentityService) — no second toast.
    if (error instanceof SessionRefreshError) {
      return throwError(() => error.message);
    }
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
      messageService.add({ severity: 'error', summary: 'Error', detail: `Message: ${errorMessage}` });
    } else {
      // Get server-side error
      // The body is ProblemDetails only when the Store API itself answered. A gateway or an auth refusal comes with
      // no body or with plain text/HTML: the text is shown as is, an empty or unreadable body — as the status text.
      // Over HTTP/2 the browser has no status text and Angular substitutes 'OK' — that is not a reason to show.
      const body = error.error;
      const problem = body !== null && typeof body === 'object' ? body : null;
      const statusText = error.statusText && error.statusText !== 'OK' ? error.statusText : 'The server returned no error details';
      errorMessage = problem?.detail ?? (typeof body === 'string' && body !== '' ? body : statusText);
      const summary = problem?.title ? `Error Code: ${error.status}. Error title: ${problem.title}.` : `Error Code: ${error.status}.`;
      messageService.add({ severity: 'error', summary, detail: `Message: ${errorMessage}` });
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}
